import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { Redirect } from "react-router-dom";
import moment from "moment";

import {
  getMonthlyChallenges,
  createMonthlyChallenge,
  addChallengeStop,
  updateMonthlyChallenge,
  deleteMonthlyChallenge,
} from "../../helpers/satiBack";
import ChallengeDrawer from "./ChallengeDrawer";

const MonthlyChallenges = () => {
  const empresaId = sessionStorage.getItem("empresaId");
  const rol = Number(sessionStorage.getItem("satiRol"));

  const [state, setState] = useState({
    isLoading: true,
    isError: false,
    errorMessage: "",
    data: [],
  });
  const [drawerState, setDrawerState] = useState({
    isOpen: false,
    challenge: null,
    error: null,
  });
  const [drawerMode, setDrawerMode] = useState("view");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    challenge: null,
  });

  useEffect(() => {
    if (empresaId !== "1" || rol !== 1) {
      return;
    }

    let isMounted = true;

    const fetchChallenges = async () => {
      try {
        const resp = await getMonthlyChallenges();

        if (!isMounted) return;

        if (resp && resp.Error) {
          setState({
            isLoading: false,
            isError: true,
            errorMessage:
              resp.ErrorMessage || "No se pudieron cargar los retos.",
            data: [],
          });
          return;
        }

        setState({
          isLoading: false,
          isError: false,
          errorMessage: "",
          data: resp && resp.Data ? resp.Data : [],
        });
      } catch (e) {
        if (!isMounted) return;
        setState({
          isLoading: false,
          isError: true,
          errorMessage: "No se pudieron cargar los retos.",
          data: [],
        });
      }
    };

    fetchChallenges();

    return () => {
      isMounted = false;
    };
  }, [empresaId, rol]);

  if (empresaId !== "1" || rol !== 1) {
    return <Redirect to="/dashboard" />;
  }

  const openDrawer = (challenge) => {
    setDrawerMode("view");
    setDrawerState({ isOpen: true, challenge, error: null });
  };

  const openEditDrawer = () => {
    setDrawerMode("edit");
    setDrawerState((prev) => ({ ...prev, error: null }));
    // Keep the same challenge data, just change mode
  };

  const openCreateDrawer = () => {
    setDrawerMode("create");
    setDrawerState({
      isOpen: true,
      challenge: { stops: [{ distanceKm: "", fact: "", order: 1 }] },
      error: null,
    });
  };

  const closeDrawer = () => {
    setDrawerState({ isOpen: false, challenge: null, error: null });
  };

  const openDeleteModal = (challenge, e) => {
    e.stopPropagation(); // Prevent row click
    setDeleteModal({ isOpen: true, challenge });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, challenge: null });
  };

  const handleDelete = async () => {
    try {
      const result = await deleteMonthlyChallenge(
        deleteModal.challenge.monthlyChallengeId,
      );

      if (result && result.Error) {
        setState((prev) => ({
          ...prev,
          isError: true,
          errorMessage: result.ErrorMessage || "Error al eliminar el reto.",
        }));
        return;
      }

      // Refresh the list
      const refreshed = await getMonthlyChallenges();
      if (refreshed && refreshed.Error) {
        setState((prev) => ({
          ...prev,
          isError: true,
          errorMessage: "Error al refrescar la lista de retos.",
        }));
        return;
      }

      setState({
        isLoading: false,
        isError: false,
        errorMessage: "",
        data: refreshed && refreshed.Data ? refreshed.Data : [],
      });

      closeDeleteModal();
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isError: true,
        errorMessage: err.message || "Error al eliminar el reto.",
      }));
    }
  };

  const handleCreate = async (e, formStops) => {
    try {
      // Clear any previous errors
      setDrawerState((prev) => ({ ...prev, error: null }));

      const formData = new FormData(e.target);
      if (!formData.has("isActive")) {
        formData.append("isActive", "false");
      }

      const challengeResult = await createMonthlyChallenge(formData);

      if (challengeResult && challengeResult.Error) {
        setDrawerState((prev) => ({
          ...prev,
          error: challengeResult.ErrorMessage || "Error al crear el reto.",
        }));
        return;
      }

      const monthlyChallengeId = challengeResult.Data.monthlyChallengeId;
      const validStops = (formStops || []).filter(
        (stop) => stop.distanceKm && stop.fact,
      );

      for (const stop of validStops) {
        await addChallengeStop(monthlyChallengeId, {
          distanceKm: parseFloat(stop.distanceKm),
          fact: stop.fact,
          order: stop.order,
        });
      }

      const refreshed = await getMonthlyChallenges();
      if (refreshed && refreshed.Error) {
        setDrawerState((prev) => ({
          ...prev,
          error: "Error al refrescar la lista de retos.",
        }));
        return;
      }

      setState({
        isLoading: false,
        isError: false,
        errorMessage: "",
        data: refreshed && refreshed.Data ? refreshed.Data : [],
      });

      closeDrawer();
    } catch (err) {
      setDrawerState((prev) => ({
        ...prev,
        error: err.message || "Error al crear el reto.",
      }));
    }
  };

  const handleUpdate = async (e, formStops) => {
    try {
      e.preventDefault();

      // Clear any previous errors
      setDrawerState((prev) => ({ ...prev, error: null }));

      const formData = new FormData(e.target);
      if (!formData.has("isActive")) {
        formData.append("isActive", "false");
      }

      // Add stops as JSON string to FormData
      if (formStops && Array.isArray(formStops)) {
        formData.append("stops", JSON.stringify(formStops));
      }

      const monthlyChallengeId = drawerState.challenge.monthlyChallengeId;
      console.log("Updating challenge:", monthlyChallengeId, formData);

      const challengeResult = await updateMonthlyChallenge(
        monthlyChallengeId,
        formData,
      );
      console.log("Update result:", challengeResult);

      if (challengeResult && challengeResult.Error) {
        setDrawerState((prev) => ({
          ...prev,
          error: challengeResult.ErrorMessage || "Error al actualizar el reto.",
        }));
        return;
      }

      const refreshed = await getMonthlyChallenges();
      if (refreshed && refreshed.Error) {
        setDrawerState((prev) => ({
          ...prev,
          error: "Error al refrescar la lista de retos.",
        }));
        return;
      }

      setState({
        isLoading: false,
        isError: false,
        errorMessage: "",
        data: refreshed && refreshed.Data ? refreshed.Data : [],
      });

      closeDrawer();
    } catch (err) {
      console.error("Error in handleUpdate:", err);
      setDrawerState((prev) => ({
        ...prev,
        error: err.message || "Error al actualizar el reto.",
      }));
    }
  };

  return (
    <div style={{ padding: "3rem" }}>
      <Row className="align-items-center mb-3">
        <Col>
          <h2 style={{ margin: 0, color: "#1D1F3F" }}>Retos mensuales</h2>
          <div style={{ color: "#666", marginTop: "0.25rem" }}>
            Administra los retos mensuales y su estado.
          </div>
        </Col>
        <Col className="text-right">
          <Button color="primary" onClick={openCreateDrawer}>
            Añadir reto
          </Button>
        </Col>
      </Row>

      <Card>
        <CardBody>
          {state.isLoading && (
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
            >
              <Spinner size="sm" color="primary" />
              <span>Cargando...</span>
            </div>
          )}

          {state.isError && (
            <Alert color="danger" style={{ marginBottom: 0 }}>
              {state.errorMessage}
            </Alert>
          )}

          {!state.isLoading && !state.isError && state.data.length === 0 && (
            <Alert color="info" style={{ marginBottom: 0 }}>
              No hay retos mensuales registrados.
            </Alert>
          )}

          {!state.isLoading && !state.isError && state.data.length > 0 && (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th>Distancia (km)</th>
                  <th>Activo</th>
                  <th>En curso</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {state.data.map((c) => (
                  <tr
                    key={c.monthlyChallengeId}
                    role="button"
                    style={{ cursor: "pointer" }}
                    onClick={() => openDrawer(c)}
                  >
                    <td>{c.name}</td>
                    <td>
                      {c.startDate
                        ? moment(c.startDate).format("YYYY-MM-DD")
                        : "-"}
                    </td>
                    <td>
                      {c.endDate ? moment(c.endDate).format("YYYY-MM-DD") : "-"}
                    </td>
                    <td>
                      {c.totalDistanceKm != null
                        ? Number(c.totalDistanceKm).toFixed(1)
                        : "-"}
                    </td>
                    <td>{c.isActive ? "Sí" : "No"}</td>
                    <td>{c.active ? "Sí" : "No"}</td>
                    <td>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={(e) => openDeleteModal(c, e)}
                        style={{ marginRight: "0.5rem" }}
                      >
                        Eliminar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <ChallengeDrawer
        isOpen={drawerState.isOpen}
        onClose={closeDrawer}
        challenge={drawerState.challenge}
        mode={drawerMode}
        title={
          drawerMode === "create"
            ? "Añadir reto"
            : drawerMode === "edit"
              ? "Editar reto"
              : "Detalle del reto"
        }
        onSubmit={
          drawerMode === "create"
            ? handleCreate
            : drawerMode === "edit"
              ? handleUpdate
              : undefined
        }
        onEdit={openEditDrawer}
        error={drawerState.error}
      />

      <Modal isOpen={deleteModal.isOpen} toggle={closeDeleteModal}>
        <ModalHeader toggle={closeDeleteModal}>
          Confirmar Eliminación
        </ModalHeader>
        <ModalBody>
          ¿Está seguro que desea eliminar el reto "{deleteModal.challenge?.name}
          "? Esta acción no se puede deshacer y eliminará todos los datos
          relacionados.
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeDeleteModal}>
            Cancelar
          </Button>
          <Button color="danger" onClick={handleDelete}>
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default MonthlyChallenges;
